import React, { Component } from 'react';
import { styled } from '@compiled/react';
import Link from '@atlaskit/link';
import { token } from '@atlaskit/tokens';
import log from '@atlassian/jira-common-util-logging/src/log';
import { FormattedMessage } from '@atlassian/jira-intl';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	REPORT_BURNUP,
	REPORT_BURNDOWN,
	REPORT_CUMULATIVE_FLOW_DIAGRAM,
	REPORT_VELOCITY,
} from '../../../common/constants';
import type { Report as ReportType } from '../../../common/types';
import messages from './messages';

type Props = {
	visible: boolean;
	reportType: ReportType;
} & Intl;

// eslint-disable-next-line jira/react/no-class-components
export class HowTo extends Component<Props> {
	static getHowToDataForReportType(reportType: ReportType) {
		switch (reportType) {
			case REPORT_BURNUP:
				return {
					whatItIs: messages.burnupReportWhat,
					howToReadIt: messages.burnupReportHow,
					learnMoreLink:
						'https://confluence.atlassian.com/jirasoftwarecloud/reports-952607296.html',
				};
			case REPORT_VELOCITY:
				return {
					whatItIs: messages.velocityReportWhat,
					howToReadIt: messages.velocityReportHow,
					learnMoreLink:
						'https://confluence.atlassian.com/jirasoftwarecloud/velocity-report-next-gen-975029957.html',
				};
			case REPORT_CUMULATIVE_FLOW_DIAGRAM:
				return {
					whatItIs: messages.cumulativeFlowWhat,
					howToReadIt: messages.cumulativeFlowHow,
					learnMoreLink:
						'https://confluence.atlassian.com/jirasoftwarecloud/cumulative-flow-diagram-next-gen-975029954.html',
				};
			case REPORT_BURNDOWN:
				return {
					whatItIs: messages.burndownWhat,
					howToReadIt: messages.burndownHow,
					learnMoreLink:
						'https://confluence.atlassian.com/display/JIRASOFTWARECLOUD/Sprint+burndown+%28next-gen%29',
				};
			default:
				// Should not be reachable
				log.safeErrorWithoutCustomerData(
					'software-reports.report-page.how-to',
					'Unknown report type how-to requested.',
				);

				return {
					whatItIs: messages.whatItIs,
					howToReadIt: messages.howToReadIt,
					learnMoreLink: '#',
				};
		}
	}

	render() {
		const {
			intl: { formatMessage },
			visible,
			reportType,
		} = this.props;

		const howToData = HowTo.getHowToDataForReportType(reportType);
		const learnMore = formatMessage(messages.learnMore);

		return (
			<HowToContainer visible={visible}>
				<Gutter>
					<b>{formatMessage(messages.whatItIs)}</b>
					<p>{formatMessage(howToData.whatItIs)}</p>
				</Gutter>
				<Gutter>
					<b>{formatMessage(messages.howToReadIt)}</b>
					<p>
						<FormattedMessage
							{...howToData.howToReadIt}
							values={{
								learnMore: (
									<Link
										onClick={(e, analyticsEvent) => {
											fireUIAnalytics(analyticsEvent, 'report-page-learn-more');
										}}
										target="_blank"
										href={howToData.learnMoreLink}
									>
										{learnMore}
									</Link>
								),
							}}
						/>
					</p>
				</Gutter>
			</HowToContainer>
		);
	}
}

export default injectIntl(HowTo);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HowToContainer = styled.div<{ visible?: boolean }>({
	display: 'flex',
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	maxHeight: (props: any) => (props.visible ? '120px' : '0px'),
	overflow: 'hidden',
	transition: 'max-height 200ms linear',
	marginBottom: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Gutter = styled.div({
	flex: '1 1 50%',
	padding: token('space.100', '8px'),
});
